import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Dialog, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { Alert } from '../../../components/alert/Alert';
import { createApiKey, CreateApiKeyResponse } from '../store/api-key.slice';

import styles from './create-api-key.modal.module.scss';
type CreateApiKeyModalProps = {
  onCancel: () => void;
  onCreated: (apiKey: CreateApiKeyResponse) => void;
};

type FormValues = {
  name: string;
  description: string;
  expiresAt: string;
};

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({ onCancel, onCreated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      name: '',
      description: '',
      expiresAt: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (isLoading) return;

    setIsLoading(true);
    setServerError('');

    try {
      const result = await createApiKey({
        name: values.name,
        description: values.description || undefined,
        expiresAt: values.expiresAt || undefined,
      });

      setCreatedKey(result);
      onCreated(result);
    } catch (error: any) {
      setServerError(error?.data?.message || 'Failed to create API key');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (createdKey) {
    return (
      <Dialog
        title="API key created"
        onClose={onCancel}
        actions={[<ElevatedButton key="done" text="Done" onClick={onCancel} />]}
      >
        <div className={styles['container']}>
          <p className={styles['warning']}>
            Make sure to copy your API key now. You won't be able to see it again.
          </p>

          <div className={styles['key-display']}>
            <code className={styles['key-value']}>{createdKey.key}</code>
            <TextButton text={isCopied ? 'Copied!' : 'Copy'} onClick={handleCopy} />
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title="Create API key"
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="create"
          type="submit"
          text="Create"
          form="create-api-key-form"
          isLoading={isLoading}
          disabled={isLoading}
        />,
      ]}
    >
      <div className={styles['container']}>
        {serverError && <Alert text={serverError} />}

        <form className={styles['form']} id="create-api-key-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <TextInput
                ref={ref}
                value={value}
                onChange={onChange}
                label="Name"
                placeholder="e.g. Production key"
                errorMessage={error?.message}
                required
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <TextInput
                ref={ref}
                value={value}
                onChange={onChange}
                label="Description"
                placeholder="What is this key used for?"
              />
            )}
          />

          <Controller
            name="expiresAt"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <TextInput
                ref={ref}
                value={value}
                onChange={onChange}
                label="Expiration date"
                placeholder="YYYY-MM-DD"
                type="date"
              />
            )}
          />
        </form>
      </div>
    </Dialog>
  );
};
